/**
 * SelectionContext for UI-Model-1
 *
 * Manages selection state only - which container, item, or submodel is selected.
 * Separated from main state to minimize re-renders on selection changes.
 *
 * Line limit: ~100 lines
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { ItemType } from '../types/state.types';
import { SelectionContextValue } from '../types/context.types';

// ============================================================================
// Context
// ============================================================================

const SelectionContext = createContext<SelectionContextValue | null>(null);

export const SelectionProvider = ({ children }: { children: React.ReactNode }) => {
  // Selection state
  const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedItemType, setSelectedItemType] = useState<ItemType | 'submodel' | null>(null);
  const [selectedSubmodelId, setSelectedSubmodelId] = useState<string | null>(null);
  const [selectionPath, setSelectionPathState] = useState<string[]>([]);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [activePageId, setActivePageId] = useState<string | null>(null);

  // Selection actions
  const selectContainer = useCallback((containerId: string, path?: string[]) => {
    setSelectedContainerId(containerId);
    setSelectedItemId(null);
    setSelectedItemType(null);
    setSelectedSubmodelId(null);
    if (path) {
      setSelectionPathState(path);
    }
  }, []);

  const selectItem = useCallback((itemId: string, itemType: ItemType, containerId?: string) => {
    setSelectedItemId(itemId);
    setSelectedItemType(itemType);
    setSelectedSubmodelId(null);
    if (containerId) {
      setSelectedContainerId(containerId);
    }
  }, []);

  const selectSubmodel = useCallback((submodelId: string, containerId?: string) => {
    setSelectedSubmodelId(submodelId);
    setSelectedItemId(null);
    setSelectedItemType('submodel');
    if (containerId) {
      setSelectedContainerId(containerId);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedContainerId(null);
    setSelectedItemId(null);
    setSelectedItemType(null);
    setSelectedSubmodelId(null);
    setSelectionPathState([]);
  }, []);

  const setSelectionPath = useCallback((path: string[]) => {
    setSelectionPathState(path);
  }, []);

  const setActivePage = useCallback((groupId: string, pageId: string) => {
    setActiveGroupId(groupId);
    setActivePageId(pageId);
    // Clear selection when changing pages
    clearSelection();
  }, [clearSelection]);

  // Selection queries
  const isContainerSelected = useCallback(
    (containerId: string) => selectedContainerId === containerId,
    [selectedContainerId]
  );

  const isItemSelected = useCallback(
    (itemId: string) => selectedItemId === itemId,
    [selectedItemId]
  );

  const isSubmodelSelected = useCallback(
    (submodelId: string) => selectedSubmodelId === submodelId,
    [selectedSubmodelId]
  );

  const value = useMemo<SelectionContextValue>(
    () => ({
      selectedContainerId,
      selectedItemId,
      selectedItemType,
      selectedSubmodelId,
      selectionPath,
      activeGroupId,
      activePageId,
      selectContainer,
      selectItem,
      selectSubmodel,
      clearSelection,
      setSelectionPath,
      setActivePage,
      isContainerSelected,
      isItemSelected,
      isSubmodelSelected,
    }),
    [
      selectedContainerId,
      selectedItemId,
      selectedItemType,
      selectedSubmodelId,
      selectionPath,
      activeGroupId,
      activePageId,
      selectContainer,
      selectItem,
      selectSubmodel,
      clearSelection,
      setSelectionPath,
      setActivePage,
      isContainerSelected,
      isItemSelected,
      isSubmodelSelected,
    ]
  );

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
};

export const useSelection = (): SelectionContextValue => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
};

export default SelectionContext;
